import { ApiState } from '@/models/generics';
import { TCallbacks } from '@/models/generics';
import { TGhostUser, TPanemUser } from '@/services/meta/models/meta';
import { TGetAddressesApiResponseData } from '@/services/user/models/addresses';
import { ProfileMenuApiResponseData } from '@/services/user/models/profile-menu.types';
import { TLocation } from '@/store/location/locationSlice';
import {
	getMetaLoading,
	getMetaSuccess,
	getMetaFailed,
	getProfileMenuLoading,
	getProfileMenuSuccess,
	getProfileMenuFailed,
	logoutLoading,
	logoutSuccess,
	logoutFailed,
	getAddressesLoading,
	getAddressesSuccess,
	getAddressesFailed,
	createAddressLoading,
	createAddressSuccess,
	createAddressFailed,
} from '@/store/user/userSlice';

import { useCart } from './useCart';
import { useCart as unlmtdUseCart } from './unlmtd/useCart';
import usePlan from './unlmtd/usePlan';
import { useLencoDispatch } from './useLencoDispatch';
import { useLencoSelector } from './useLencoSelector';
import { useWishlist } from './useWishlist';
import { metaService } from '@/services/meta';
import { userService } from '@/services/user';
import { authService } from '@/services/auth';

type TUseUser = {
	getUserApiState: ApiState;
	getAddressessApiState: ApiState;
	ghostUser?: TGhostUser;
	user?: TPanemUser;
	isLoggedIn: boolean;
	loadingProfileMenu: boolean;
	addresses?: TGetAddressesApiResponseData;
	profileMenu?: ProfileMenuApiResponseData;
	userActions: {
		getUser: (location?: TLocation) => Promise<any>;
		logoutUser: () => Promise<any>;
		getProfileMenu: () => Promise<any>;
		getAddresses: (callback: () => void) => Promise<any>;
		createAddress: (payload: any, callbacks?: TCallbacks) => Promise<any>;
	};
};

export function useUser(): TUseUser {
	const {
		user,
		ghostUser,
		profileMenu,
		addresses,
		getUserApiState,
		getAddressessApiState,
		loadingProfileMenu,
	} = useLencoSelector((state: any) => state.user);

	const { planActions, selectedPlanId } = usePlan();
	const dispatch = useLencoDispatch();
	const { cartActions } = useCart();
	const unlmtdCart = unlmtdUseCart();
	const { wishlistActions } = useWishlist();

	const getUser = async (location?: TLocation, callback?: Function) => {
		try {
			dispatch(getMetaLoading());
			const res = await metaService.getMeta(location);
			const meta = res.data?.data?.meta;
			const isLoggedIn = !!user?.id;
			dispatch(
				getMetaSuccess({
					user: meta?.user,
					ghost_user: meta?.ghost_user,
				})
			);

			if (window.location.pathname.includes('unlmtd')) {
				if (selectedPlanId) {
					unlmtdCart?.cartActions?.fetchCart();
				}
				planActions.getPlans();
			} else {
				// cartActions.getCart();
			}
			dispatch(getProfileMenuLoading());
			getProfileMenu();

			if (isLoggedIn) {
				wishlistActions.getWishlists();
			}
			if (!!callback) {
				callback();
			}

			return true;
		} catch (error) {
			dispatch(getMetaFailed());
			return false;
		}
	};

	const logoutUser = async () => {
		try {
			dispatch(logoutLoading());
			const res = await authService.logOutUser();
			dispatch(logoutSuccess(res?.data?.data));
			unlmtdCart?.cartActions?.clearCart();

			// dispatch(resetWishlist());
			getUser();
			cartActions?.getCart();
			return true;
		} catch (error) {
			dispatch(logoutFailed());
			return false;
		}
	};

	const getProfileMenu = async () => {
		try {
			dispatch(getProfileMenuLoading());
			const res = await userService.getUserProfileMenu();
			dispatch(getProfileMenuSuccess(res.data?.data));
			return true;
		} catch (error) {
			dispatch(getProfileMenuFailed());
			return false;
		}
	};

	const getAddresses = async (callback: () => void) => {
		try {
			dispatch(getAddressesLoading());
			const res = await userService.getAddress();
			dispatch(getAddressesSuccess(res?.data?.data));
			if (callback) callback();
			return true;
		} catch (error) {
			dispatch(getAddressesFailed());
			return false;
		}
	};
	const createAddress = async (payload: any, callbacks?: TCallbacks) => {
		try {
			dispatch(createAddressLoading());
			const res = await userService.addAddress(payload);

			if (callbacks?.finally) {
				callbacks.finally();
			}

			if (res.data?.success) {
				dispatch(createAddressSuccess());
				return true;
			} else {
				throw new Error('Failed to create address');
			}
		} catch (error) {
			dispatch(createAddressFailed());
			if (callbacks?.finally) {
				callbacks.finally();
			}
			return false;
		}
	};
	const isLoggedIn = !!user?.id;

	return {
		getUserApiState,
		getAddressessApiState,
		user,
		ghostUser,
		isLoggedIn,
		profileMenu,
		addresses,
		loadingProfileMenu,
		userActions: {
			getUser,
			logoutUser,
			getAddresses,
			getProfileMenu,
			createAddress,
		},
	};
}
